import { AxiosError } from 'axios';
import { apiErrorMessage, applicantApi, publicApi } from './api';
import { IPlaidVerification, IncomeSuccessRequest, IncomeSuccessResponse, IncomeRequest } from './plaid.types';

/** The API's own envelope; failures are normalised to `status: 'fail'` so callers check one field. */
export interface ApiResult {
  status: 'success' | 'fail';
  message?: string;
  data?: unknown;
}

export const getApplicantFromToken = async (token: string) => {
  try {
    const response = await applicantApi.get('/application/applicant', {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  } catch (err) {
    console.log('error', (err as AxiosError).message);
  }
};

export const fetchLinkToken = async (incomeBody: IncomeRequest) => {
  try {
    const response = await applicantApi.post('/plaid/generate_link_token', incomeBody);
    return response;
  } catch (err) {
    console.log('Not able to generate link token', (err as AxiosError).message);
  }
};

export const setIncomeSuccessful = async (body: IncomeSuccessRequest): Promise<IncomeSuccessResponse | undefined> => {
  try {
    const response = await applicantApi.post('/plaid/income_was_successful', body);
    return response.data;
  } catch (err) {
    console.log('Not able to update income connected', (err as AxiosError).message);
  }
};

export const startPlaidVerification = async (body: IPlaidVerification) => {
  try {
    const response = await applicantApi.post('/plaid/verification', body);
    return response.data;
  } catch (err) {
    return apiErrorMessage(err, 'Verification failed.');
  }
};

export const recordPlaidLinkSession = async (body: { applicantId: string | undefined; sessionEvent: any }) => {
  try {
    await applicantApi.post('/plaid/record_link_session', body);
  } catch (err) {
    console.log('recordPlaidLinkSession error (non-fatal)', (err as AxiosError).message);
  }
};

export interface ContactSupportRequest {
  message: string;
  applicant_id?: string;
  application_id: string;
}

export const contactSupport = async (body: ContactSupportRequest): Promise<ApiResult> => {
  try {
    const response = await applicantApi.post(`/application/contact-support`, body);
    return response.data;
  } catch (err) {
    return { status: 'fail', message: apiErrorMessage(err, 'Unable to send message to support team.') };
  }
};

export const submitDocuments = async (files: File[], applicantId: string, applicationId: string, employerEmail?: string): Promise<ApiResult> => {
  try {
    const data = new FormData();
    files.forEach(file => data.append('files', file));
    if (employerEmail) data.append('employer_email', employerEmail);
    const response = await applicantApi.post(`/application/application/${applicationId}/applicant/${applicantId}/submit-documents`, data);
    return response.data;
  } catch (err) {
    return { status: 'fail', message: apiErrorMessage(err, 'Unable to submit documents.') };
  }
};

// ── Employer Verification of Employment (VOE) — public, token-scoped ──
// publicApi, not applicantApi: these carry the employer's token, never the applicant's.
export const getEmployerVoeContext = async (token: string) => {
  try {
    const response = await publicApi.get('/application/employer-verification', {
      headers: { Authorization: token },
    });
    return response.data;
  } catch (err) {
    return { status: 'fail', message: (err as any).response?.data?.message || 'Unable to load this verification.' };
  }
};

export interface EmployerVoeSubmission {
  file: File;
  verifier_name: string;
  verifier_title: string;
  monthly_income?: string;
  annual_income?: string;
  employment_type: string;
  employment_start_date: string;
}

export const submitEmployerVerification = async (token: string, payload: EmployerVoeSubmission) => {
  try {
    const data = new FormData();
    data.append('file', payload.file);
    data.append('token', token);
    data.append('verifier_name', payload.verifier_name);
    data.append('verifier_title', payload.verifier_title);
    if (payload.monthly_income) data.append('monthly_income', payload.monthly_income);
    if (payload.annual_income) data.append('annual_income', payload.annual_income);
    data.append('employment_type', payload.employment_type);
    data.append('employment_start_date', payload.employment_start_date);
    const response = await publicApi.post('/application/employer-verification/submit', data);
    return response.data;
  } catch (err) {
    return { status: 'fail', message: (err as any).response?.data?.message || 'Unable to submit verification.' };
  }
};
